import React, { useState } from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";

import { cn, Icon } from "@flare/ui";

import type { BuilderTextProps } from "../Typography/Text/Text";
import { BuilderText } from "../Typography/Text/Text";

import type { FaqsProps } from "./FaqsV2";

type TitleProps = {
  iconColor?: BuilderTextProps["color"];
  isOpen: boolean;
};

export function Title({
  children,
  isOpen,
  iconColor,
}: React.PropsWithChildren<TitleProps>) {
  /**
   * This is a workaround to use the action color when we get primary in the iconColor prop
   *
   * TODO: Remove this once we migrate the FAQ iconColor prop to the new color palette on Builder
   */
  const color = iconColor === "primary" ? "action" : iconColor;

  return (
    <CollapsiblePrimitive.Trigger asChild>
      <button className="flex w-full items-start justify-between">
        {children}
        {/* Using the BuilderText component to inherit the color prop on the icon */}
        <BuilderText as="span" color={color}>
          {isOpen ? (
            <Icon size="lg" name="minus" />
          ) : (
            <Icon size="lg" name="plus" />
          )}
        </BuilderText>
      </button>
    </CollapsiblePrimitive.Trigger>
  );
}

type FaqItemProps = Omit<FaqsProps, "event" | "items"> & {
  answer: string;
  bodyVariant: BuilderTextProps["variant"];
  isOpen: boolean;
  onItemClicked: ({ isOpen }: { isOpen: boolean }) => void;
  question: React.ReactNode | string;
  questionTextVariant?: BuilderTextProps["variant"];
  textColor?: BuilderTextProps["color"];
};

export function FaqItem({
  answer,
  question,
  textColor,
  questionTextVariant = "text-1",
  bodyVariant = "text-3",
  onItemClicked,
  isOpen,
  iconColor,
}: FaqItemProps) {
  const [isClicked, setIsClicked] = useState(false);

  function toggle() {
    setIsClicked(true);
    onItemClicked({
      isOpen: !isOpen,
    });
  }

  return (
    <CollapsiblePrimitive.Root
      className="border-t border-primary py-[40px] last:border-b last:border-primary"
      open={isOpen}
      onOpenChange={toggle}
    >
      <Title isOpen={isOpen} iconColor={iconColor}>
        <BuilderText variant={questionTextVariant} color={textColor}>
          {question}
        </BuilderText>
      </Title>
      <CollapsiblePrimitive.Content
        className={cn(
          "w-full max-w-[1100px] overflow-hidden transition-all data-[state=closed]:animate-faq-close data-[state=open]:animate-faq-open",
          isOpen && isClicked ? "mt-6 [&_*_a]:underline" : "hidden",
        )}
        forceMount
      >
        <BuilderText
          color={textColor}
          variant={bodyVariant}
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}
